import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Alert, Card, CardBody, Col, Container, Row, Spinner } from "../../Components/Common/Tailwind";

import { REST_API_KEY } from "../../common/social/kakao/api"
import { KAKAO_AUTH_URL } from "../../api/auth";
import { authService } from "../../api/services/auth.service";
import MobileViewContainer from "../../Components/MobileViewContainer";
import { useAuthContext } from "../../Components/AuthContext";
import { useUserStore } from "../../store/store";

const KakaoPage = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [site] = useUserStore((state) => [state.site]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // 결정 303: 카카오가 전화번호를 주지 않으면 동의 화면으로 다시 로그인
  const [needPhoneConsent, setNeedPhoneConsent] = React.useState(false);
  const hasProcessedCode = React.useRef(false);

  const { loginSocial, isUserLogin, isLoading: authLoading, error: authError } = useAuthContext();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const KAKAO_CODE = params.get('code');
    const state = params.get('state');

    if (authLoading) {
      // 인증 로딩 중인 경우, 아무 작업도 하지 않음
      return;
    }

    if (isUserLogin) {
      // 이미 로그인한 상태에서 카카오 페이지에 접근한 경우
      const redirectTo = window.localStorage.getItem("redirectTo");
      if (redirectTo && redirectTo.trim()) {
        window.localStorage.removeItem("redirectTo");
        navigate(`${redirectTo}`);
      } else {
        navigate(`/`);
      }
      return;
    }

    if (!navigate || !loginSocial) {
      return;
    }

    if (!KAKAO_CODE || !state) {
      setError("URL 파싱에 실패했습니다. 다시 시도해주세요.");
      return;
    }

    if (hasProcessedCode.current) {
      return;
    }
    hasProcessedCode.current = true;

    const processKakaoLogin = async () => {
      setIsLoading(true);

      try {
        // Step 1: Exchange Kakao code for access token
        const response = await authService.exchangeKakaoCode(KAKAO_CODE, REST_API_KEY);

        if (!response.data?.access_token) {
          throw new Error("MISSING_TOKEN");
        }

        const kakaoAccessToken = response.data.access_token;

        // Step 2: Call our backend with the Kakao token to check if phone is required
        const backendResponse = await authService.postKakaoToken(kakaoAccessToken);

        if (backendResponse.data?.type === 'reconsent_required') {
          // 결정 459-2: 새 로그인 동의 1회 — 서버가 카카오 연결을 끊었으니 바로 카카오 로그인(동의 화면)을 다시 연다
          window.location.replace(KAKAO_AUTH_URL);
          return;
        }

        if (backendResponse.data?.type === 'phone_consent_required') {
          setNeedPhoneConsent(true);
          setError(backendResponse.data.message || "전화번호 동의가 필요해요. 다시 로그인해 주세요.");
          return;
        }

        // Check if this is a phone_required response
        if (backendResponse.data?.type === 'phone_required') {
          const kakaoToken = backendResponse.data.kakao_access_token;
          window.localStorage.setItem("kakao_pending_token", kakaoToken);
          navigate("/auth/kakao-phone-input", {
            state: {
              kakao_access_token: kakaoToken,
              kakao_user_info: backendResponse.data.kakao_user_info
            }
          });
          return;
        }

        // Step 3: Complete login through loginSocial (which will use the same token)
        await loginSocial("kakao", kakaoAccessToken);

        const redirectTo = window.localStorage.getItem("redirectTo");
        if (redirectTo && redirectTo.trim()) {
          window.localStorage.removeItem("redirectTo");
          navigate(`${redirectTo}`);
        } else {
          navigate(`/`);
        }

      } catch (error) {
        console.log(error);

        const errType = error.response?.data?.type;
        if (errType === 'phone_consent_required' || errType === 'phone_conflict') {
          setNeedPhoneConsent(errType === 'phone_consent_required');
          setError(error.response.data.message || error.response.data.detail);
          return;
        }

        // Check if this is a phone_required response (in case it's thrown as error)
        if (error.response?.data?.type === 'phone_required') {
          // Store Kakao token and redirect to phone input page
          const kakaoToken = error.response.data.kakao_access_token;
          window.localStorage.setItem("kakao_pending_token", kakaoToken);
          navigate("/auth/kakao-phone-input", {
            state: {
              kakao_access_token: kakaoToken,
              kakao_user_info: error.response.data.kakao_user_info
            }
          });
          return;
        }

        if (error.response && error.response?.data) {
          let errorData = error.response.data;
          if (typeof errorData === 'string') {
            setError("카카오 로그인에 실패했습니다. 다시 시도해주세요. " + errorData);
          } else if (typeof errorData === 'object') {
            setError("카카오 로그인에 실패했습니다. 다시 시도해주세요. " +
              JSON.stringify(errorData) +
              " -- " + KAKAO_CODE + " " + REST_API_KEY);
          } else {
            setError("카카오 로그인에 실패했습니다. 다시 시도해주세요. " + "알 수 없는 오류");
          }
        } else {
          setError("카카오 로그인에 실패했습니다. 다시 시도해주세요. " + error.message);
        }
      } finally {
        setIsLoading(false);
        hasProcessedCode.current = false;
      }
    };
    processKakaoLogin();
  }, [location, loginSocial, isUserLogin, authLoading, hasProcessedCode.current]);

  // https://devtalk.kakao.com/t/koe320-an-authorization-code-must-be-supplied-authorization-code-not-found/126910
  //   * 이미 사용(만료)된 인가코드를 재사용 한 경우
  // {"error":"invalid_grant",
  // "error_description":"authorization code not found for code=xNey_oPAr4XUOj8TNkSEp0xYUT6q_qLwJcgfdeNMX8Eh-EwHqHMN1tdKzyXK0IC1ySexoAoqJZAAAAGFUyG0bw",
  // "error_code":"KOE320"}


  return (
    <React.Fragment>
      <MobileViewContainer>
        <div className="flex h-screen items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            {/* Top Section: Logo & Welcome */}
            <div className="w-full mt-5 px-4 text-center">
              <div className="mb-4 flex justify-center">
                <Link to="/" className="inline-block">
                  <img
                    src={site?.logo ? site.logo : `/amazing-logo.png`}
                    alt="Logo"
                    className="rounded-full"
                    style={{
                      height: "72px",
                      width: "auto",
                      maxWidth: "100%",
                      objectFit: "contain",
                      filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.08))"
                    }}
                  />
                </Link>
              </div>
              <h2 className="font-bold mb-2 text-center" style={{ color: "#1A1A1A", fontSize: "22px", letterSpacing: "-1px" }}>
                {site?.name || "어메이징 BBQ"}
              </h2>
              <p className="text-gray-500 mb-0 text-center" style={{ fontSize: "16px", fontWeight: "400", opacity: 0.8 }}>
                {site?.slogan || "세상에 없던 놀이터, 놀다 지쳐 쓰러지리라!"}
              </p>
            </div>

            <Row className="mt-4">
              <Col lg={12}>
                <Card className="bg-white rounded shadow-md">
                  <CardBody>
                    <h4 className="text-center">카카오 로그인</h4>

                    <div className="mb-4 mt-5 ">

                      {isLoading && (
                        <div className="p-2">
                          <div className="text-center">
                            <Spinner color="primary" />
                          </div>
                          <div className="text-center">
                            <p>
                              로그인을 처리중입니다. 잠시만 기다려주세요.
                            </p>
                          </div>
                        </div>
                      )}

                      {!isLoading && error && (
                        <div className="text-center">
                          <Alert color="danger">{error}</Alert>
                          {needPhoneConsent && (
                            <a href={`${KAKAO_AUTH_URL}&scope=phone_number`} className="inline-block mt-3 px-4 py-2 rounded font-bold" style={{ background: "#FEE500", color: "#191919" }}>
                              전화번호 동의하고 다시 로그인
                            </a>
                          )}
                        </div>
                      )}
                      {authError && (
                        <Alert color="danger">{authError}</Alert>
                      )}

                    </div>
                    <div className="p-2">
                      <Link to="/login" className="text-gray-500">
                        <i className="mdi mdi-account-circle"></i> 로그인 화면으로 돌아가기
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </MobileViewContainer>
    </React.Fragment>
  );
};

export default KakaoPage;
